import { isAxiosError } from 'axios';
import { router, useLocalSearchParams } from 'expo-router';
import { PhoneIncoming, Plus } from 'lucide-react-native';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FlatList,
  Modal,
  Platform,
  StyleSheet,
  useWindowDimensions,
  View,
  type ViewStyle,
} from 'react-native';
import { FadeIn } from '../../../../src/components/animations';
import { Box, HStack, Text, VStack } from '../../../../src/components/primitives';
import { Button } from '../../../../src/components/primitives/Button';
import { EmptyState } from '../../../../src/components/primitives/EmptyState';
import { Select } from '../../../../src/components/primitives/Select';
import { Sheet } from '../../../../src/components/primitives/Sheet';
import { Body, Caption, Heading4, Label } from '../../../../src/components/primitives/Text';
import { useAuth } from '../../../../src/contexts/AuthContext';
import { useEdgeToEdge } from '../../../../src/contexts/LayoutChromeContext';
import { useTranslation } from '../../../../src/contexts/TranslationContext';
import { CallFilters } from '../../../../src/features/voice-agents/components/CallFilters';
import { CallListItem } from '../../../../src/features/voice-agents/components/CallListItem';
import { CallListSkeleton } from '../../../../src/features/voice-agents/components/CallListSkeleton';
import { CallMetrics } from '../../../../src/features/voice-agents/components/CallMetrics';
import { VoiceSectionTabs } from '../../../../src/features/voice-agents/components/VoiceSectionTabs';
import { describeCallRefusal } from '../../../../src/features/voice-agents/lib/callRefusal';
import {
  useCallStats,
  useDeleteVoiceCall,
  useTriggerVoiceCall,
  useVoiceAgents,
  useVoiceCalls,
} from '../../../../src/features/voice-agents/hooks';
import type {
  CallingHoursWindow,
  ListVoiceCallsFilters,
  VoiceCall,
  VoiceCallOutcome,
  VoiceCallStatus,
} from '../../../../src/features/voice-agents/types';
import { buildAgentOptions } from '../../../../src/features/voice-agents/utils/agentOptionLabels';
import {
  formatCallingHours,
  isLeadOutsideCallingHours,
  resolveCallingTimezone,
} from '../../../../src/features/voice-agents/utils/callingHours';
import { useLeadConsent } from '../../../../src/hooks/useConsent';
import { useLeads } from '../../../../src/hooks/useLeads';
import { useResponsive } from '../../../../src/hooks/useResponsive';
import { colors } from '../../../../src/theme/colors';
import { tokens } from '../../../../src/theme/tokens';

export function TriggerModal({
  visible,
  organizationId,
  onClose,
  initialLeadId,
}: {
  visible: boolean;
  organizationId: string;
  onClose: () => void;
  initialLeadId?: string;
}) {
  const { t } = useTranslation();
  const { isDesktop, platform } = useResponsive();
  const [agentId, setAgentId] = useState('');
  const [leadId, setLeadId] = useState(initialLeadId ?? '');
  const wasVisibleRef = useRef(visible);
  const trigger = useTriggerVoiceCall();

  useEffect(() => {
    if (visible && !wasVisibleRef.current) {
      setAgentId('');
      setLeadId(initialLeadId ?? '');
    }
    wasVisibleRef.current = visible;
  }, [visible, initialLeadId]);

  const agentsQuery = useVoiceAgents(organizationId, { status: 'published' });
  const leadsQuery = useLeads({ organizationId, limit: 100 }, !!organizationId && visible);
  const consentQuery = useLeadConsent(leadId.trim() || undefined);

  // FLOSEAI-560 — mirrors the backend trigger gate: a lead with no consent on
  // record is a legitimate cold call (the agent asks for consent during the
  // call). Only an explicit refusal blocks the trigger, so only that state may
  // disable the button — anything stricter would hide a call the API accepts.
  const consentResolved = !!leadId.trim() && consentQuery.isSuccess;
  const consentBlocked = consentResolved && consentQuery.data?.dataProcessing?.granted === false;

  const selectedAgent = (agentsQuery.data ?? []).find((a) => a.id === agentId.trim());
  const selectedLead = (leadsQuery.data?.leads ?? []).find((l) => l.id === leadId.trim());
  const callingHoursBlocked =
    !!agentId.trim() && !!leadId.trim() && isLeadOutsideCallingHours(selectedAgent, selectedLead);

  const callingHoursTimezone = callingHoursBlocked
    ? resolveCallingTimezone(selectedAgent, selectedLead)
    : null;
  const dayLabel = useCallback(
    (day: CallingHoursWindow['day']) =>
      t(`voiceAgents.wizard.callingHours.day${day.charAt(0).toUpperCase()}${day.slice(1)}`),
    [t],
  );
  const callingHoursSchedule =
    callingHoursBlocked && callingHoursTimezone && selectedAgent?.callingHours
      ? formatCallingHours(selectedAgent.callingHours, callingHoursTimezone, dayLabel)
      : '';

  // FLOSEAI-656 — two agents from the same template share a name, and this
  // dropdown decides which one places the call.
  const agentOptions = useMemo(() => buildAgentOptions(agentsQuery.data ?? []), [agentsQuery.data]);
  const leadOptions = useMemo(
    () =>
      (leadsQuery.data?.leads ?? [])
        .filter((l) => l.phone)
        .map((l) => ({ value: l.id, label: `${l.fullName ?? l.phone} · ${l.phone}` })),
    [leadsQuery.data],
  );

  const handleSubmit = useCallback(() => {
    if (!agentId.trim() || !leadId.trim()) return;
    trigger.mutate(
      { voiceAgentId: agentId.trim(), leadId: leadId.trim(), organizationId },
      {
        onSuccess: () => {
          setAgentId('');
          setLeadId('');
          onClose();
        },
      },
    );
  }, [agentId, leadId, organizationId, trigger, onClose]);

  const position = platform === 'web' && isDesktop ? 'center' : 'bottom';

  return (
    <Sheet
      visible={visible}
      onClose={onClose}
      title={t('voiceAgents.callsList.triggerCall')}
      position={position}
      size="md"
    >
      <View accessibilityLabel={t('voiceAgents.callsList.triggerCall')}>
        <VStack spacing="md">
          <VStack spacing="xs">
            <Label style={styles.inputLabel}>{t('voiceAgents.callsList.agentSelectLabel')}</Label>
            <Select
              variant="dropdown"
              options={agentOptions}
              value={agentId}
              onChange={setAgentId}
              placeholder={t('voiceAgents.callsList.selectAgentPlaceholder')}
              testID="trigger-agent-select"
              searchable
              searchPlaceholder={t('common.selectSearchPlaceholder')}
              emptySearchLabel={t('common.selectNoMatches')}
            />
            {!agentsQuery.isLoading && agentOptions.length === 0 ? (
              <Caption style={styles.errorText}>
                {t('voiceAgents.callsList.noPublishedAgents')}
              </Caption>
            ) : null}
          </VStack>
          <VStack spacing="xs">
            <Label style={styles.inputLabel}>{t('voiceAgents.callsList.leadSelectLabel')}</Label>
            <Select
              variant="dropdown"
              options={leadOptions}
              value={leadId}
              onChange={setLeadId}
              placeholder={t('voiceAgents.callsList.selectLeadPlaceholder')}
              testID="trigger-lead-select"
              searchable
              searchPlaceholder={t('common.selectSearchPlaceholder')}
              emptySearchLabel={t('common.selectNoMatches')}
            />
            {!leadsQuery.isLoading && leadOptions.length === 0 ? (
              <Caption style={styles.errorText}>{t('voiceAgents.callsList.noLeads')}</Caption>
            ) : null}
            {consentBlocked ? (
              <Caption
                style={styles.warningText}
                accessibilityLiveRegion="polite"
                testID="trigger-consent-warning"
              >
                {t('voiceAgents.callsList.consentRejectedError')}
              </Caption>
            ) : null}
            {callingHoursBlocked ? (
              <Caption
                style={styles.warningText}
                accessibilityLiveRegion="polite"
                testID="trigger-calling-hours-warning"
              >
                {t('voiceAgents.callsList.outsideCallingHoursWarning')}
              </Caption>
            ) : null}
            {callingHoursBlocked && callingHoursSchedule ? (
              <Caption
                style={styles.warningText}
                accessibilityLiveRegion="polite"
                testID="trigger-calling-hours-schedule"
              >
                {callingHoursSchedule}
              </Caption>
            ) : null}
          </VStack>
        </VStack>
        <HStack
          spacing="md"
          style={{ marginTop: tokens.spacing.lgPlus, justifyContent: 'flex-end' }}
        >
          <Button
            variant="outline"
            size="sm"
            onPress={onClose}
            accessibilityLabel={t('voiceAgents.callsList.cancel')}
          >
            {t('voiceAgents.callsList.cancel')}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onPress={handleSubmit}
            accessibilityLabel={t('voiceAgents.callsList.confirmTrigger')}
            accessibilityState={{
              busy: trigger.isPending,
              disabled: trigger.isPending || consentBlocked || callingHoursBlocked,
            }}
            disabled={trigger.isPending || consentBlocked || callingHoursBlocked}
          >
            {trigger.isPending
              ? t('voiceAgents.callsList.triggering')
              : t('voiceAgents.callsList.trigger')}
          </Button>
        </HStack>
        {trigger.isError ? (
          <Body
            style={styles.errorText}
            accessibilityLiveRegion="assertive"
            accessibilityRole="alert"
          >
            {/* A 409 covers every policy refusal — do-not-call, rejected
                consent, calling hours, the daily cap — and used to collapse
                into one sentence that named none of them. The backend now
                sends a code and, for the cap, the moment it frees, so the
                refusal can say which rule applied and until when
                (FLOSEAI-679). The operator message is never rendered. */}
            {isAxiosError(trigger.error) && trigger.error.response?.status === 409
              ? (() => {
                  const refusal = describeCallRefusal(trigger.error);
                  return t(refusal.key, refusal.values);
                })()
              : `${t('voiceAgents.callsList.errorPrefix')} ${
                  trigger.error?.message ?? t('voiceAgents.callsList.unknownError')
                }`}
          </Body>
        ) : null}
      </View>
    </Sheet>
  );
}

const OUTCOME_VALUES: VoiceCallOutcome[] = [
  'qualified',
  'not_qualified',
  'callback_requested',
  'meeting_booked',
  'do_not_call',
  'no_answer',
  'voicemail_left',
  'technical_failure',
  'incomplete',
];

type CallOrPlaceholder = VoiceCall | { id: string; __placeholder: true };

function padCallsForGrid(calls: VoiceCall[], columns: number): CallOrPlaceholder[] {
  if (columns <= 1 || calls.length % columns === 0) return calls;
  return [
    ...calls,
    ...Array.from({ length: columns - (calls.length % columns) }, (_, index) => ({
      id: `__call_placeholder_${String(index)}`,
      __placeholder: true as const,
    })),
  ];
}

export default function CallInboxScreen() {
  useEdgeToEdge();
  const { t } = useTranslation();
  const { currentOrganization } = useAuth();
  const { width: viewportWidth } = useWindowDimensions();
  const orgId = currentOrganization?.id ?? '';
  const numColumns = viewportWidth >= tokens.breakpoints.desktop ? 2 : 1;

  const { triggerLeadId } = useLocalSearchParams<{ triggerLeadId?: string }>();
  const deepLinkLeadId = typeof triggerLeadId === 'string' ? triggerLeadId : undefined;

  const [statusFilter, setStatusFilter] = useState<VoiceCallStatus | ''>('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [country, setCountry] = useState('');
  const [agentFilter, setAgentFilter] = useState('');
  const [leadFilter, setLeadFilter] = useState('');
  const [outcomeFilter, setOutcomeFilter] = useState<VoiceCallOutcome | ''>('');
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [modalVisible, setModalVisible] = useState(!!deepLinkLeadId);
  const [triggerLeadPreset, setTriggerLeadPreset] = useState<string | undefined>(deepLinkLeadId);
  const [callToDelete, setCallToDelete] = useState<VoiceCall | null>(null);

  const openTriggerModal = useCallback((leadId?: string) => {
    setTriggerLeadPreset(leadId);
    setModalVisible(true);
  }, []);

  const closeTriggerModal = useCallback(() => {
    setModalVisible(false);
    setTriggerLeadPreset(undefined);
  }, []);

  const agentsQuery = useVoiceAgents(orgId);
  const leadsQuery = useLeads({ organizationId: orgId, limit: 100 }, !!orgId);

  const agentOptions = useMemo(
    () => [
      { value: '', label: t('voiceAgents.callsList.filterSelectAll') },
      ...buildAgentOptions(agentsQuery.data ?? []),
    ],
    [agentsQuery.data, t],
  );

  const leadOptions = useMemo(
    () => [
      { value: '', label: t('voiceAgents.callsList.filterSelectAll') },
      ...(leadsQuery.data?.leads ?? []).map((l) => ({
        value: l.id,
        label: l.fullName ?? l.phone ?? l.id,
      })),
    ],
    [leadsQuery.data, t],
  );

  const outcomeOptions = useMemo(
    () => [
      { value: '', label: t('voiceAgents.callsList.filterSelectAll') },
      ...OUTCOME_VALUES.map((o) => ({
        value: o,
        label: t(`voiceAgents.callsList.outcomeFilter.${o}`),
      })),
    ],
    [t],
  );

  const filters: ListVoiceCallsFilters = useMemo(
    () => ({
      ...(statusFilter ? { status: statusFilter } : {}),
      ...(fromDate ? { from: fromDate } : {}),
      ...(toDate ? { to: toDate } : {}),
      ...(country.trim() ? { country: country.trim() } : {}),
      ...(agentFilter ? { voiceAgentId: agentFilter } : {}),
      ...(leadFilter ? { leadId: leadFilter } : {}),
      ...(outcomeFilter ? { outcome: outcomeFilter } : {}),
    }),
    [agentFilter, country, fromDate, leadFilter, outcomeFilter, statusFilter, toDate],
  );

  const { data, isLoading, isError, refetch } = useVoiceCalls(orgId, filters);
  const statsQuery = useCallStats(orgId);
  const calls = data?.data ?? [];
  const isResolvingOrg = !orgId;
  const activeFilterCount = Object.keys(filters).length;

  const clearFilters = useCallback(() => {
    setStatusFilter('');
    setFromDate('');
    setToDate('');
    setCountry('');
    setAgentFilter('');
    setLeadFilter('');
    setOutcomeFilter('');
  }, []);

  return (
    <View style={styles.root} testID="calls-screen">
      <View style={styles.content}>
        <FadeIn delay={0} translateY={12} style={styles.sectionSpacing}>
          <HStack justify="between" align="center" wrap style={styles.toolbarRow}>
            <VStack spacing="xs" style={styles.toolbarTitle}>
              <Text
                variant="h4"
                color={colors.text.primary}
                style={styles.heading}
                accessibilityRole="header"
              >
                {t('voiceAgents.callsList.title')}
              </Text>
              <Text variant="caption" color={colors.text.tertiary}>
                {data?.total ?? 0} {t('voiceAgents.callsList.results')}
              </Text>
            </VStack>
            <Button
              variant="primary"
              size="sm"
              onPress={() => openTriggerModal()}
              accessibilityLabel={t('voiceAgents.callsList.triggerNewCall')}
            >
              <HStack spacing="xs" align="center">
                <Plus size={15} color={colors.background.primary} strokeWidth={2.4} />
                <Text
                  variant="caption"
                  color={colors.background.primary}
                  style={styles.actionLabel}
                >
                  {t('voiceAgents.callsList.triggerCall')}
                </Text>
              </HStack>
            </Button>
          </HStack>
        </FadeIn>

        <FadeIn delay={40} translateY={10} style={styles.sectionSpacing}>
          <VoiceSectionTabs />
        </FadeIn>

        <FadeIn delay={80} translateY={10} style={styles.sectionSpacing}>
          <CallMetrics stats={statsQuery.data} loading={statsQuery.isLoading || isResolvingOrg} />
        </FadeIn>

        <FadeIn delay={120} translateY={10} style={styles.sectionSpacing}>
          <CallFilters
            expanded={filtersExpanded}
            onToggle={() => setFiltersExpanded((expanded) => !expanded)}
            activeCount={activeFilterCount}
            onClear={clearFilters}
            status={statusFilter}
            onStatusChange={setStatusFilter}
            fromDate={fromDate}
            onFromDateChange={setFromDate}
            toDate={toDate}
            onToDateChange={setToDate}
            country={country}
            onCountryChange={setCountry}
            agent={agentFilter}
            onAgentChange={setAgentFilter}
            lead={leadFilter}
            onLeadChange={setLeadFilter}
            outcome={outcomeFilter}
            onOutcomeChange={setOutcomeFilter}
            agentOptions={agentOptions}
            leadOptions={leadOptions}
            outcomeOptions={outcomeOptions}
          />
        </FadeIn>

        <Box flex={1}>
          {isResolvingOrg || isLoading ? (
            <View
              style={styles.loadingState}
              testID="calls-loading"
              accessible
              accessibilityLabel={t('voiceAgents.callsList.loading')}
              accessibilityLiveRegion="polite"
            >
              <CallListSkeleton />
            </View>
          ) : isError ? (
            <View
              style={styles.center}
              testID="calls-error"
              accessibilityLabel={t('voiceAgents.callsList.errorTitle')}
            >
              <EmptyState
                icon={<PhoneIncoming size={36} color={colors.text.tertiary} strokeWidth={1.5} />}
                title={t('voiceAgents.callsList.errorTitle')}
                description={t('voiceAgents.callsList.errorDescription')}
              />
              <Button
                variant="secondary"
                testID="calls-retry"
                accessibilityLabel={t('voiceAgents.callsList.retry')}
                onPress={() => refetch()}
              >
                {t('voiceAgents.callsList.retry')}
              </Button>
            </View>
          ) : calls.length === 0 ? (
            <View
              style={styles.center}
              testID="calls-empty"
              accessibilityLabel={t('voiceAgents.callsList.emptyTitle')}
            >
              <EmptyState
                icon={<PhoneIncoming size={36} color={colors.text.tertiary} strokeWidth={1.5} />}
                title={
                  activeFilterCount > 0
                    ? t('voiceAgents.callsList.emptyTitle')
                    : t('voiceAgents.callsList.firstEmptyTitle')
                }
                description={
                  activeFilterCount > 0
                    ? t('voiceAgents.callsList.emptyDescription')
                    : t('voiceAgents.callsList.firstEmptyDescription')
                }
                action={
                  activeFilterCount > 0
                    ? { label: t('voiceAgents.callsList.clearFilters'), onPress: clearFilters }
                    : {
                        label: t('voiceAgents.callsList.triggerCall'),
                        onPress: () => openTriggerModal(),
                      }
                }
              />
            </View>
          ) : (
            <FlatList<CallOrPlaceholder>
              key={`calls-${String(numColumns)}`}
              data={padCallsForGrid(calls, numColumns)}
              keyExtractor={(call) => call.id}
              numColumns={numColumns}
              columnWrapperStyle={numColumns > 1 ? styles.columnWrapper : undefined}
              renderItem={({ item }) => {
                if ('__placeholder' in item) return <View style={styles.gridItem} />;
                const card = (
                  <CallListItem
                    call={item}
                    onPress={(call) => router.push(`/voice-agents/calls/${call.id}`)}
                    onDelete={setCallToDelete}
                  />
                );
                return numColumns > 1 ? <View style={styles.gridItem}>{card}</View> : card;
              }}
              ItemSeparatorComponent={
                numColumns === 1 ? () => <View style={styles.listSeparator} /> : undefined
              }
              contentContainerStyle={styles.list}
              contentInsetAdjustmentBehavior="automatic"
              accessibilityLabel={t('voiceAgents.callsList.listA11y')}
            />
          )}
        </Box>
      </View>

      <TriggerModal
        visible={modalVisible}
        organizationId={orgId}
        initialLeadId={triggerLeadPreset}
        onClose={closeTriggerModal}
      />

      <DeleteCallModal
        call={callToDelete}
        organizationId={orgId}
        onClose={() => setCallToDelete(null)}
      />
    </View>
  );
}

function DeleteCallModal({
  call,
  organizationId,
  onClose,
}: {
  call: VoiceCall | null;
  organizationId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const del = useDeleteVoiceCall();
  const visible = !!call;

  useEffect(() => {
    if (visible) del.reset();
  }, [visible, del.reset]);

  function handleConfirm() {
    if (!call) return;
    del.mutate({ id: call.id, organizationId }, { onSuccess: onClose });
  }

  const who = call?.lead?.fullName ?? call?.toNumber ?? '';

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.modalOverlay}>
        <View style={styles.modalCard} accessibilityLabel={t('voiceAgents.callsList.deleteTitle')}>
          <Heading4 style={styles.modalTitle} accessibilityRole="header">
            {t('voiceAgents.callsList.deleteTitle')}
          </Heading4>
          <Body style={styles.deleteBody}>{`${t('voiceAgents.callsList.deleteBody')} ${who}`}</Body>

          {del.isError ? (
            <Body
              style={styles.errorText}
              accessibilityLiveRegion="assertive"
              accessibilityRole="alert"
            >
              {del.error?.message ?? t('voiceAgents.callsList.deleteError')}
            </Body>
          ) : null}

          <HStack spacing="md" style={styles.deleteFooter}>
            <Button
              variant="outline"
              size="sm"
              onPress={onClose}
              disabled={del.isPending}
              accessibilityLabel={t('voiceAgents.callsList.cancel')}
            >
              {t('voiceAgents.callsList.cancel')}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onPress={handleConfirm}
              loading={del.isPending}
              disabled={del.isPending}
              style={styles.deleteConfirmBtn}
              accessibilityLabel={t('voiceAgents.callsList.deleteConfirm')}
            >
              {t('voiceAgents.callsList.deleteConfirm')}
            </Button>
          </HStack>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    position: 'relative',
    ...(Platform.OS === 'web'
      ? ({ overflow: 'hidden', width: '100%', maxWidth: '100%' } as ViewStyle)
      : {}),
  },
  content: {
    flex: 1,
    flexDirection: 'column',
    paddingHorizontal: tokens.spacing.lg,
    paddingTop: tokens.spacing.lg,
    ...(Platform.OS === 'web'
      ? ({ minWidth: 0, width: '100%', maxWidth: '100%' } as ViewStyle)
      : {}),
  },
  toolbarRow: {
    rowGap: tokens.spacing.sm,
    width: '100%',
  },
  toolbarTitle: {
    flexShrink: 1,
    minWidth: 0,
  },
  heading: {
    fontWeight: tokens.fontWeight.bold,
  },
  actionLabel: {
    fontWeight: tokens.fontWeight.semibold,
  },
  sectionSpacing: {
    marginBottom: tokens.spacing.md,
  },
  list: {
    paddingVertical: tokens.spacing.xs,
    paddingBottom: tokens.spacing['3xl'],
  },
  listSeparator: {
    height: tokens.spacing.sm,
  },
  columnWrapper: {
    marginBottom: tokens.spacing.md,
    columnGap: tokens.spacing.md,
  },
  gridItem: {
    flex: 1,
    flexBasis: 0,
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
  },
  loadingState: {
    flex: 1,
    paddingTop: tokens.spacing.xs,
  },
  center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacing['2xl'],
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: colors.background.overlay,
    justifyContent: 'flex-end',
  },
  modalCard: {
    backgroundColor: colors.background.secondary,
    borderTopLeftRadius: tokens.radius.xlPlus,
    borderTopRightRadius: tokens.radius.xlPlus,
    padding: tokens.spacing.xl,
    paddingBottom: tokens.spacing.xxlPlus,
    borderTopWidth: 1,
    borderColor: colors.border.subtle,
  },
  modalTitle: {
    color: colors.text.primary,
  },
  inputLabel: {
    color: colors.text.secondary,
    fontSize: tokens.fontSize.sm,
  },
  errorText: {
    color: colors.status.error.main,
    marginTop: tokens.spacing.md,
    fontSize: tokens.fontSize.smPlus,
  },
  warningText: {
    color: colors.status.warning.main,
    marginTop: tokens.spacing.xs,
    fontSize: tokens.fontSize.sm,
  },
  deleteBtn: {
    padding: tokens.spacing.xs,
    borderRadius: tokens.radius.sm,
  },
  deleteBody: {
    color: colors.text.secondary,
    marginTop: tokens.spacing.md,
    lineHeight: 22,
  },
  deleteFooter: {
    marginTop: tokens.spacing.lgPlus,
    justifyContent: 'flex-end',
  },
  deleteConfirmBtn: {
    backgroundColor: colors.status.error.main,
    borderColor: colors.status.error.main,
  },
});
